import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { Box, Drawer, List, Typography } from "@mui/material";
import { matchPath, useLocation } from "react-router-dom";
import { NavbarMenuItem } from "../molecules/navbar-menu/navbar-menu-item";

import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useSiteEvChargersModuleAccess } from "src/features/site-ev-chargers/hooks/api/use-site-ev-chargers-module-access";
import { getRoutes as getSiteRoutes } from "src/features/site/constants/routes";
import { useTenantEvChargersModuleAccess } from "src/features/tenant-ev-chargers/hooks/api/use-tenant-ev-chargers-module-access";
import { getRoutes } from "src/features/tenant/constants/routes";
import useStore from "src/hooks/use-store";
import { ViewContext } from "src/store/user-store";
import { Route } from "src/types/interfaces/route";
import { OrganizationSelector } from "./organization-selector";
import { SiteSelector } from "./site-selector";
import { getSecurityRoutes } from "src/features/security/constants/routes";
import { usePermissions } from "src/permissions/can";

interface NavbarMenuProps {
  onClose: () => void;
  open: boolean;
}

const getRoutesForViewContext = (
  currentViewContext: ViewContext,
  hasAccessToEVChargers: boolean,
  hasAccessToSystemMessages: boolean,
) => {
  switch (currentViewContext) {
    case "site":
      return getSiteRoutes(hasAccessToEVChargers, hasAccessToSystemMessages);

    case "tenant":
      return getRoutes(hasAccessToEVChargers);

    case "security":
      return getSecurityRoutes();
  }
};

const workAreaSelector = (viewContext: ViewContext) => {
  switch (viewContext) {
    case "site":
      return <SiteSelector />;

    case "tenant":
      return <OrganizationSelector />;

    default:
      return null;
  }
};

const NavbarMenuComponent: FC<NavbarMenuProps> = props => {
  const { t } = useTranslation();
  const { open, onClose } = props;
  const { pathname } = useLocation();
  const { user } = useStore();
  const permissions = usePermissions();

  const { data: siteEvChargersModuleAccess } = useSiteEvChargersModuleAccess();
  const { data: tenantEvChargersModuleAccess } = useTenantEvChargersModuleAccess();

  const sections = useMemo(
    () =>
      getRoutesForViewContext(
        user.viewContext,
        user.viewContext === "site"
          ? siteEvChargersModuleAccess?.hasAccessToEVChargers
          : tenantEvChargersModuleAccess?.hasAccessToEVChargers,
        permissions.can("read", "SystemMessages"),
      ),
    [
      user.viewContext,
      siteEvChargersModuleAccess?.hasAccessToEVChargers,
      tenantEvChargersModuleAccess?.hasAccessToEVChargers,
      permissions,
    ],
  );
  const routes = useMemo(() => sections.flatMap(route => route.routes), [sections]);

  const [activeRoute, setActiveRoute] = useState<Route | null>(null);

  useEffect(() => {
    routes.forEach(route => {
      const active = matchPath({ path: route.href, end: false }, pathname);

      if (active) {
        setActiveRoute(route);
      }
    });
  }, [pathname]);

  return (
    <Drawer
      anchor="top"
      onClose={onClose}
      open={open}
      transitionDuration={0}
      ModalProps={{
        BackdropProps: {
          invisible: true,
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: "background.paper",
          color: "#B2B7C8",
          display: "flex",
          flexDirection: "column",
          top: 64,
          maxHeight: "calc(100% - 64px)",
          width: "100vw",
        },
      }}>
      <List>
        {workAreaSelector(user.viewContext)}
        {sections.map(section => (
          <Box key={section.title}>
            <Typography
              sx={{
                fontSize: "14px",
                my: 1,
                px: 1,
              }}
              color="#98a2b3">
              {t(section.title)}
            </Typography>
            {section.routes.map(route => (
              <NavbarMenuItem
                active={activeRoute?.title === route.title}
                key={route.title}
                href={route.href}
                onClose={onClose}
                {...route}
              />
            ))}
          </Box>
        ))}
      </List>
    </Drawer>
  );
};

export const NavbarMenu = observer(NavbarMenuComponent);
